/**
 * API Service Layer
 *
 * This module provides a centralized service layer for all API calls to the
 * Firebase Cloud Functions backend. All services handle authentication
 * automatically using Firebase Auth tokens.
 */

import { firebaseAuth as auth } from '../lib/firebase/client';
import type {
  Project,
  ProjectWithStats,
  CreateProjectInput,
  UpdateProjectInput,
  Design,
  DesignWithStats,
  CreateDesignInput,
  UpdateDesignInput,
  Question,
  CreateQuestionInput,
  UpdateQuestionInput,
  ReorderQuestionsInput,
  Candidate,
  CreateCandidateInput,
  UpdateCandidateInput,
  Participant,
  ParticipantWithCandidate,
  AddParticipantsInput,
  UpdateParticipantInput,
  InterviewResult,
  SaveResultInput,
  ProjectInsights,
  GenerateQuestionsInput,
  GeneratedQuestion,
  AnalyzeTranscriptInput,
  AnalysisResult,
  ChatMessage,
  ExportOptions,
  ExportResult,
  PaginatedResponse,
} from '../types';

// Use production API directly to avoid proxy issues in development
// This bypasses the Vite proxy which seems to have issues
const API_BASE = import.meta.env.DEV
  ? 'https://decoa-livekit.web.app/api'
  : '/api';

console.log('[API] Using base URL:', API_BASE);

// =============================================================================
// API Request Helper
// =============================================================================

/**
 * Get authentication headers with Firebase ID token
 */
async function getAuthHeaders(): Promise<HeadersInit> {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User not authenticated');
  }

  const token = await user.getIdToken();
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
}

/**
 * Generic API request handler with error handling
 */
async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const headers = await getAuthHeaders();

  const url = `${API_BASE}${endpoint}`;
  console.log('[API] Requesting:', options.method || 'GET', url);

  const response = await fetch(url, {
    ...options,
    headers: {
      ...headers,
      ...options.headers,
    },
  });

  console.log('[API] Response status:', response.status, response.statusText);

  if (!response.ok) {
    const text = await response.text();
    console.error('[API] Error response:', text);

    // Try to parse as JSON, fallback to text
    let error;
    try {
      error = JSON.parse(text);
    } catch {
      error = { error: text || 'Request failed' };
    }
    throw new Error(error.error || error.message || `HTTP ${response.status}`);
  }

  // Handle empty responses
  const text = await response.text();
  if (!text) {
    return {} as T;
  }

  try {
    return JSON.parse(text);
  } catch (e) {
    console.error('[API] Failed to parse JSON response:', text.substring(0, 200));
    throw new Error('Invalid JSON response from server');
  }
}

// =============================================================================
// Projects Service
// =============================================================================

export const projectsService = {
  /**
   * Get all projects for the current user
   */
  getAll: (): Promise<{ projects: ProjectWithStats[] }> =>
    apiRequest('/projects'),

  /**
   * Get a single project by ID with its designs
   */
  getById: (id: string): Promise<{ project: Project; designs: DesignWithStats[] }> =>
    apiRequest(`/projects/${id}`),

  /**
   * Create a new project
   */
  create: (data: CreateProjectInput): Promise<Project> =>
    apiRequest('/projects', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  /**
   * Update an existing project
   */
  update: (id: string, data: UpdateProjectInput): Promise<{ success: boolean }> =>
    apiRequest(`/projects/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  /**
   * Delete a project and all its data
   */
  delete: (id: string): Promise<{ success: boolean }> =>
    apiRequest(`/projects/${id}`, {
      method: 'DELETE',
    }),

  /**
   * Get designs in a project
   */
  getDesigns: (projectId: string): Promise<{ designs: Design[] }> =>
    apiRequest(`/projects/${projectId}/designs`),

  /**
   * Create a new design in a project
   */
  createDesign: (projectId: string, data: CreateDesignInput): Promise<Design> =>
    apiRequest(`/projects/${projectId}/designs`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  /**
   * Get participants in a project
   */
  getParticipants: (projectId: string): Promise<{ participants: ParticipantWithCandidate[] }> =>
    apiRequest(`/projects/${projectId}/participants`),

  /**
   * Add participants to a project
   */
  addParticipants: (projectId: string, data: AddParticipantsInput): Promise<{ added: number }> =>
    apiRequest(`/projects/${projectId}/participants`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  /**
   * Get interview results for a project
   */
  getResults: (projectId: string): Promise<{ results: InterviewResult[] }> =>
    apiRequest(`/projects/${projectId}/results`),

  /**
   * Get project insights
   */
  getInsights: (projectId: string): Promise<{ insights: ProjectInsights | null }> =>
    apiRequest(`/projects/${projectId}/insights`),

  /**
   * Generate/regenerate project insights
   */
  generateInsights: (projectId: string): Promise<{ insights: ProjectInsights }> =>
    apiRequest(`/projects/${projectId}/insights`, {
      method: 'POST',
    }),

  /**
   * Export project data
   */
  export: (projectId: string, options: ExportOptions): Promise<ExportResult> =>
    apiRequest(`/projects/${projectId}/export`, {
      method: 'POST',
      body: JSON.stringify(options),
    }),
};

// =============================================================================
// Designs Service
// =============================================================================

export const designsService = {
  /**
   * Get a design by ID with its questions
   */
  getById: (id: string): Promise<{ design: DesignWithStats; questions: Question[] }> =>
    apiRequest(`/designs/${id}`),

  /**
   * Update a design
   */
  update: (id: string, data: UpdateDesignInput): Promise<{ success: boolean }> =>
    apiRequest(`/designs/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  /**
   * Delete a design
   */
  delete: (id: string): Promise<{ success: boolean }> =>
    apiRequest(`/designs/${id}`, {
      method: 'DELETE',
    }),

  /**
   * Get questions for a design
   */
  getQuestions: (designId: string): Promise<{ questions: Question[] }> =>
    apiRequest(`/designs/${designId}/questions`),

  /**
   * Add a question to a design
   */
  addQuestion: (designId: string, data: CreateQuestionInput): Promise<Question> =>
    apiRequest(`/designs/${designId}/questions`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  /**
   * Batch update questions (for reordering)
   */
  reorderQuestions: (designId: string, data: ReorderQuestionsInput): Promise<{ success: boolean }> =>
    apiRequest(`/designs/${designId}/questions`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
};

// =============================================================================
// Questions Service
// =============================================================================

export const questionsService = {
  /**
   * Get a single question
   */
  getById: (id: string): Promise<{ question: Question }> =>
    apiRequest(`/questions/${id}`),

  /**
   * Update a question
   */
  update: (id: string, data: UpdateQuestionInput): Promise<{ success: boolean }> =>
    apiRequest(`/questions/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  /**
   * Delete a question
   */
  delete: (id: string): Promise<{ success: boolean }> =>
    apiRequest(`/questions/${id}`, {
      method: 'DELETE',
    }),
};

// =============================================================================
// Candidates Service
// =============================================================================

export const candidatesService = {
  /**
   * Get all candidates with optional search and pagination
   */
  getAll: (params?: {
    search?: string;
    limit?: number;
    offset?: number;
  }): Promise<PaginatedResponse<Candidate>> => {
    const query = new URLSearchParams();
    if (params?.search) query.set('search', params.search);
    if (params?.limit) query.set('limit', params.limit.toString());
    if (params?.offset) query.set('offset', params.offset.toString());

    const queryString = query.toString();
    return apiRequest(`/candidates${queryString ? `?${queryString}` : ''}`);
  },

  /**
   * Get a single candidate by ID
   */
  getById: (id: string): Promise<{
    candidate: Candidate;
    participations: Array<Participant & { projectName: string }>;
  }> => apiRequest(`/candidates/${id}`),

  /**
   * Create a new candidate
   */
  create: (data: CreateCandidateInput): Promise<Candidate> =>
    apiRequest('/candidates', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  /**
   * Update a candidate
   */
  update: (id: string, data: UpdateCandidateInput): Promise<{ success: boolean }> =>
    apiRequest(`/candidates/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  /**
   * Delete a candidate
   */
  delete: (id: string): Promise<{ success: boolean }> =>
    apiRequest(`/candidates/${id}`, {
      method: 'DELETE',
    }),
};

// =============================================================================
// Participants Service
// =============================================================================

export const participantsService = {
  /**
   * Get participant details
   */
  getById: (id: string): Promise<{
    participant: Participant;
    candidate: Candidate | null;
    design: Design | null;
    interviewResult: InterviewResult | null;
  }> => apiRequest(`/participants/${id}`),

  /**
   * Update participant status
   */
  update: (id: string, data: UpdateParticipantInput): Promise<{ success: boolean }> =>
    apiRequest(`/participants/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  /**
   * Remove participant from project
   */
  delete: (id: string): Promise<{ success: boolean }> =>
    apiRequest(`/participants/${id}`, {
      method: 'DELETE',
    }),
};

// =============================================================================
// Results Service
// =============================================================================

export const resultsService = {
  /**
   * Save a new interview result
   */
  save: (data: SaveResultInput): Promise<InterviewResult> =>
    apiRequest('/results', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  /**
   * Get a single result by ID
   */
  getById: (id: string): Promise<{
    result: InterviewResult & { candidateName: string; designName: string };
  }> => apiRequest(`/results/${id}`),
};

// =============================================================================
// AI Service
// =============================================================================

// =============================================================================
// User Settings Service
// =============================================================================

export const userSettingsService = {
  /**
   * Get current user settings
   */
  get: (): Promise<{
    interviewerName: string;
    displayName: string;
    language: string;
    timezone: string;
  }> => apiRequest('/user-settings'),

  /**
   * Update user settings
   */
  update: (data: {
    interviewerName?: string;
    displayName?: string;
    language?: 'ja' | 'en';
    timezone?: string;
  }): Promise<{ success: boolean }> =>
    apiRequest('/user-settings', {
      method: 'PUT',
      body: JSON.stringify(data),
    }),
};

// =============================================================================
// AI Service
// =============================================================================

export const aiService = {
  /**
   * Generate interview questions using AI
   */
  generateQuestions: (params: GenerateQuestionsInput): Promise<{ questions: GeneratedQuestion[] }> =>
    apiRequest('/ai/generate-questions', {
      method: 'POST',
      body: JSON.stringify(params),
    }),

  /**
   * Analyze an interview transcript
   */
  analyzeTranscript: (params: AnalyzeTranscriptInput | { resultId: string }): Promise<AnalysisResult> =>
    apiRequest('/ai/analyze-transcript', {
      method: 'POST',
      body: JSON.stringify(params),
    }),

  /**
   * Send a message to the AI analyst
   */
  chat: (
    projectId: string,
    message: string,
    conversationHistory: ChatMessage[]
  ): Promise<{ reply: string }> =>
    apiRequest('/ai/chat', {
      method: 'POST',
      body: JSON.stringify({ projectId, message, conversationHistory }),
    }),

  /**
   * Derive prompt variables from generation parameters using AI
   * Fetches system prompt from Langfuse and calls OpenAI to generate variables
   */
  derivePromptVariables: async (params: {
    target: string;
    topic: string;
    goal: string;
    usage: string;
  }): Promise<{
    primary_goal: string;
    secondary_goal: string;
    contextual_domain_notes: string;
    product_service: string;
  }> => {
    console.log('[aiService.derivePromptVariables] Input:', params);
    const result = await apiRequest<{
      primary_goal: string;
      secondary_goal: string;
      contextual_domain_notes: string;
      product_service: string;
    }>('/ai/derive-prompt-variables', {
      method: 'POST',
      body: JSON.stringify(params),
    });
    console.log('[aiService.derivePromptVariables] Result:', result);
    return result;
  },
};

// =============================================================================
// Diary Users Service (Candidate Management)
// =============================================================================

export interface DiaryUser {
  id: string;
  hasProfile: boolean;
  displayName: string;
  email: string;
  // Bilingual fields
  attributesJa: string | null;
  attributesEn: string | null;
  analysisTagsJa: string[];
  analysisTagsEn: string[];
  totalConversations: number;
  aiDiarySummaryJa: string;
  aiDiarySummaryEn: string;
  // Conversation starter prompt for main card (Flutter app)
  conversationStarterJa: string | null;
  conversationStarterEn: string | null;
  // Recent conversations summary (for dashboard)
  recentConversationsSummaryJa: string | null;
  recentConversationsSummaryEn: string | null;
  moodTrendJa: string | null;
  moodTrendEn: string | null;
  jobCareerJa: string | null;
  jobCareerEn: string | null;
  familyStructureJa: string | null;
  familyStructureEn: string | null;
  hobbiesInterestsJa: string[];
  hobbiesInterestsEn: string[];
  coreValuesJa: string[];
  coreValuesEn: string[];
  lifeHistoryJa: string | null;
  lifeHistoryEn: string | null;
  recentTopicsJa: string[];
  recentTopicsEn: string[];
  lastAnalyzedAt: unknown;
  updatedAt: unknown;
}

export interface DiaryConversation {
  id: string;
  title: string;
  status: string;
  createdAt: unknown;
  updatedAt?: unknown;
  // Bilingual
  titleJa?: string | null;
  titleEn?: string | null;
  summaryJa: string | null;
  summaryEn: string | null;
  mood?: string | null;
  // Tags from predefined themes list (replaces themes/key_points)
  tagsJa?: string[];
  tagsEn?: string[];
  // Legacy fields for backward compatibility
  themesJa?: string[];
  themesEn?: string[];
  keyPointsJa?: string[];
  keyPointsEn?: string[];
  fullTranscript?: string | null;
}


export const diaryUsersService = {
  /**
   * List all diary users with profiles
   */
  getAll: (params?: {
    search?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ users: DiaryUser[]; total: number; hasMore: boolean }> => {
    const query = new URLSearchParams();
    if (params?.search) query.set('search', params.search);
    if (params?.limit) query.set('limit', params.limit.toString());
    if (params?.offset) query.set('offset', params.offset.toString());
    const queryString = query.toString();
    return apiRequest(`/diary-users${queryString ? `?${queryString}` : ''}`);
  },

  /**
   * Get a single user's full profile with recent conversations
   */
  getById: (userId: string): Promise<{
    user: DiaryUser;
    recentConversations: DiaryConversation[];
  }> => apiRequest(`/diary-users/${userId}`),

  /**
   * Get all conversations for a user
   */
  getConversations: (userId: string): Promise<{
    conversations: DiaryConversation[];
    total: number;
  }> => apiRequest(`/diary-users/${userId}/conversations`),

  /**
   * Get a single conversation with full analysis and messages
   */
  getConversation: (userId: string, conversationId: string): Promise<{
    conversation: Record<string, unknown>;
    messages: Array<{ id: string; role: string; content: string; createdAt: unknown }>;
  }> => apiRequest(`/diary-users/${userId}/conversations/${conversationId}`),
};

// =============================================================================
// Export all services
// =============================================================================

export const api = {
  projects: projectsService,
  designs: designsService,
  questions: questionsService,
  candidates: candidatesService,
  participants: participantsService,
  results: resultsService,
  ai: aiService,
  userSettings: userSettingsService,
  diaryUsers: diaryUsersService,
};

export default api;
